import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import classNames from 'classnames';
import { type ReactNode, useContext } from 'react';
import { useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import {
  Button,
  ButtonGroup,
  Divider,
  Popover,
  Text,
  useDisclosure,
} from '@revelio/ui';

import { WithTooltip } from '../../shared/components/with-tooltip';
import { CustomReportBuilderContext } from '../reports/report-builder/custom-report-builder-provider';
import { reportBuilderStore } from '../reports/report-builder/report-builder.repository';
import { useDataEnrichmentActions } from './data-enrichment/data-enrichment.store';
import styles from './deliverable-step-container.module.css';
import themeStyles from './deliverable-step-container-theme.module.css';

interface DeliverableStepContainerProps {
  id: string;
  title: string;
  subtitle?: string;
  titleElement?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  previousStepPath?: string;
  submitButtonContent?: ReactNode;
  submitTooltip?: ReactNode;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
  isSubmitHidden?: boolean;
  skipable?: boolean;
  onSkip?: () => void;
  onSubmit?: () => void;
  disableFade?: boolean;
  isDataBuilder?: boolean;
  onReset?: () => void;
  startOverPath: string;
  isReportStep?: boolean;
}

export const DeliverableStepContainer = ({
  id,
  title,
  subtitle,
  titleElement,
  banner,
  children,
  previousStepPath,
  submitButtonContent = 'Continue',
  submitTooltip,
  isSubmitDisabled = false,
  isSubmitLoading = false,
  isSubmitHidden = false,
  skipable = false,
  onSkip,
  onSubmit,
  disableFade,
  isDataBuilder = true,
  onReset = () => null,
  startOverPath,
  isReportStep = false,
}: DeliverableStepContainerProps) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setReportTemplate } = useContext(CustomReportBuilderContext);
  const { resetState } = useDataEnrichmentActions();

  const isColumnsAndFiltersSelectStep = id === 'columns_filters_select';

  const onClickStartOver = () => {
    // Report Builder
    if (isReportStep) {
      reportBuilderStore.actions.reset(); // reset middleware reports selections
      setReportTemplate(null); // reset custom reports selections
    }
    // Data Builder
    else {
      resetState();
    }
    navigate(startOverPath);
    onClose();
    onReset();
  };
  const ContinueButton = onSubmit ? (
    <Button
      className={`${themeStyles.continueButton} ${themeStyles.continueButtonSizing}`}
      onClick={onSubmit}
      disabled={isSubmitDisabled}
      loading={isSubmitLoading}
      hidden={isSubmitHidden}
      color="primary"
      size="sm"
    >
      {submitButtonContent}
    </Button>
  ) : null;

  const StartOverBreadcrumb = isDataBuilder ? (
    <BreadcrumbItem data-testid="start-over">
      <Popover
        placement="top-start"
        arrow
        open={isOpen}
        onOpenChange={(next) => (next ? onOpen() : onClose())}
        // "Start over" navigates away, so handing focus back to the breadcrumb
        // as the surface closes would land it on a link the new step no longer
        // shows.
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={themeStyles.startOverPopover}
        trigger={
          <BreadcrumbLink
            className={themeStyles.startOver}
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
          >
            Start over
          </BreadcrumbLink>
        }
      >
        <div className={themeStyles.startOverHeader}>
          <span className={themeStyles.startOverText}>Confirm Action</span>
          <button
            type="button"
            aria-label="Close"
            className={themeStyles.startOverClose}
            onClick={onClose}
          >
            <IconCrossSmall size={16} ariaHidden={true} />
          </button>
        </div>
        <div className={themeStyles.startOverText}>
          Are you sure you want to start over? This will reset all your changes.
        </div>
        <div className={themeStyles.startOverFooter}>
          <ButtonGroup size="sm">
            <Button
              className={themeStyles.startOverCancel}
              variant="outlined"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-start-over"
              color="destructive"
              size="sm"
              onClick={onClickStartOver}
            >
              Confirm
            </Button>
          </ButtonGroup>
        </div>
      </Popover>
    </BreadcrumbItem>
  ) : null;

  // "Back" sits next to "Continue" on the right (per Figma).
  const PreviousStepLink = previousStepPath ? (
    <NavLink
      to={previousStepPath}
      data-testid="previous-step-themed"
      className={`${themeStyles.backLink} ${styles.backLink}`}
    >
      Back
    </NavLink>
  ) : null;

  const SkipButton = skipable ? (
    <Button
      onClick={onSkip}
      className={themeStyles.skipButtonSizing}
      color="primary"
      variant="outlined"
      size="sm"
    >
      Skip
    </Button>
  ) : null;

  return (
    <div
      className={styles.stepRoot}
      data-testid={`deliverables-${id}-box-container`}
    >
      <div className={styles.stepLayout}>
        <div className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.titleGroup}>
              <Text
                as="h2"
                className={classNames(
                  styles.stepHeading,
                  themeStyles.stepHeading
                )}
                fontSize="md"
                weight="semibold"
              >
                {title}
              </Text>
              {subtitle ? (
                <Text
                  className={classNames(styles.subtitle, themeStyles.subtitle)}
                  weight="semibold"
                  fontSize="md"
                  tone="muted"
                >
                  {subtitle}
                </Text>
              ) : null}
            </div>
            {banner ? (
              <div className={styles.banner}>{banner}</div>
            ) : (
              <div className={styles.headerSpacer} />
            )}
            {titleElement ? (
              <div className={styles.titleSlot}>{titleElement}</div>
            ) : null}
          </div>
        </div>

        <div
          className={`${styles.stepContent} ${
            disableFade
              ? ''
              : // biome-ignore lint/style/noNestedTernary: migrated from develop
                isColumnsAndFiltersSelectStep
                ? styles.dataBuilderContainer
                : styles.contentContainer
          }`}
        >
          {children}
        </div>

        <Divider
          className={classNames(themeStyles.footerDivider, styles.footerRule)}
        />

        <div className={styles.footer}>
          <div>
            <Breadcrumb
              className={themeStyles.footerBreadcrumb}
              color="gray.500"
              separator="|"
            >
              {StartOverBreadcrumb}
            </Breadcrumb>
          </div>
          <div className={styles.footerActions}>
            {PreviousStepLink}
            {id === 'companies' && skipable ? (
              <div className={styles.skipActions}>
                {SkipButton}
                {ContinueButton}
              </div>
            ) : (
              <WithTooltip
                tooltipId="deliverable_continue_blocked"
                label={submitTooltip}
                showCondition={!!submitTooltip}
              >
                {ContinueButton}
              </WithTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
